import React, { Component } from 'react';
import {
  graphql,
  createPaginationContainer,
  type RelayPaginationProp,
} from 'react-relay';
import { bindActionCreators } from 'redux';
import { formValueSelector, initialize, reset } from 'redux-form';
import { TableRow, TableRowColumn } from 'material-ui/Table';
import CreateIcon from 'material-ui/svg-icons/content/create';
import IconButton from 'material-ui/IconButton';
import RaisedButton from 'material-ui/RaisedButton';
import Moment from 'react-moment';

import { Grid, Loader } from '../../components';
import Well from '../../components/Well';
import JobRequestsFilter from './JobRequestsFilter';
import enhancer from '../enhancer';

const JobRequestStatus = ({ value }) => (
  <span>{value.replace('JobRequestStatus.', '')}</span>
);

const JobRequestType = ({ value }) => (
  <span>{value.replace('JobRequestType.', '')}</span>
);

const styles = {
  toolbarButton: {
    padding: 5,
  },
};

const TimeSlot = ({ startDate, duration }) => (
  <span>
    <Moment date={startDate} format="D MMM" />
    <span>{` - ${duration.hours} H ${duration.minutes} M`}</span>
  </span>
);

const PAGE_SIZE = 20;

type Props = {} & RelayPaginationProp;

type State = {
  error: ?Error,
  loading: boolean,
};

class JobRequests extends Component<Props, State> {
  state = {
    loading: false,
    error: null,
  };

  handleLoadMore = () => {
    this.setState({ loading: true });
    if (!this.props.relay.hasMore() || this.props.relay.isLoading()) {
      return;
    }

    this.props.relay.loadMore(PAGE_SIZE, error =>
      this.setState({ loading: false, error })
    );
  };

  handleReload = () => {
    this.setState({ loading: true });
    this.props.relay.refetchConnection(PAGE_SIZE, error =>
      this.setState({ loading: false, error })
    );
  };

  handleEdit = (event, jobRequestId) => {
    this.props.history.push(`/admin/job-request/${jobRequestId}`);
    event.preventDefault();
  };

  handleResetFilter = () => {
    this.props.actions.reset('jobRequestsForm');
  };

  handleFilter = () => {
    const { hasOpenJobOffers, student, ...filters } = this.props.filterValues;

    this.setState({ loading: true });

    this.props.relay.refetchConnection(
      PAGE_SIZE,
      error => this.setState({ loading: false, error }),
      {
        ...filters,
        hasOpenJobOffers: hasOpenJobOffers ? true : null,
        studentId: student ? student.value : null,
      }
    );

    this.props.actions.initialize('jobRequestsForm', this.props.filterValues);
  };

  renderRow = ({ node }) => (
    <TableRow key={node.id}>
      <TableRowColumn>{`${node.student.firstname} ${
        node.student.lastname
      }`}</TableRowColumn>
      <TableRowColumn>
        <JobRequestStatus value={node.status} />
      </TableRowColumn>
      <TableRowColumn>
        <JobRequestType value={node.requestType} />
      </TableRowColumn>
      <TableRowColumn>{node.openJobOffersCount}</TableRowColumn>
      <TableRowColumn>
        {node.sessions.map(({ timeSlots }) =>
          timeSlots.map(timeSlot => (
            <span key={timeSlot.id}>
              <TimeSlot {...timeSlot} />
            </span>
          ))
        )}
      </TableRowColumn>
      <TableRowColumn>{node.subject.name}</TableRowColumn>
      <TableRowColumn>{node.grade.name}</TableRowColumn>

      <TableRowColumn>
        <IconButton
          href={`/admin/job-request/${node.id}`}
          onClick={event => {
            this.handleEdit(event, node.id);
          }}
        >
          <CreateIcon />
        </IconButton>
      </TableRowColumn>
    </TableRow>
  );

  render() {
    const { admin } = this.props;
    const { allJobRequests, allStudents } = admin;
    const { edges, pageInfo } = allJobRequests;

    const { error, loading } = this.state;

    const headers = [
      'Student',
      'Status',
      'Type',
      'Offers Count',
      'TimeSlots',
      'Subject',
      'Grade',
      'Actions',
    ];

    return (
      <Well>
        <form>
          <JobRequestsFilter
            handleFilter={this.handleFilter}
            handleResetFilter={this.handleResetFilter}
            filterValues={this.props.filterValues}
            allStudents={allStudents}
          />

          {loading ? <Loader /> : null}
          {error && <div>{error.messge}</div>}

          <Grid headers={headers} renderRow={this.renderRow} rows={edges} />
        </form>

        <span style={styles.toolbarButton}>
          <RaisedButton
            disabled={!pageInfo.hasNextPage || loading}
            primary
            label="Load More"
            onClick={this.handleLoadMore}
          />
        </span>
        <span style={styles.toolbarButton}>
          <RaisedButton
            disabled={loading}
            secondary
            label="Reload"
            onClick={this.handleReload}
          />
        </span>
      </Well>
    );
  }
}

const selector = formValueSelector('jobRequestsForm');

const query = graphql`
  query JobRequestsAdminQuery(
    $count: Int!
    $cursor: String
    $hasOpenJobOffers: Boolean
    $searchStudent: String
    $status: [JobRequestStatus]
    $requestType: [JobRequestType]
    $studentId: ID
  ) {
    admin {
      ...JobRequests_admin @arguments(count: $count, cursor: $cursor)
    }
  }
`;

export default enhancer(JobRequests, {
  form: {
    form: 'jobRequestsForm',
  },
  mapStateToProps: state => ({
    filterValues: selector(
      state,
      'status',
      'requestType',
      'hasOpenJobOffers',
      'student'
    ),
  }),
  mapDispatchToProps: dispatch => ({
    actions: bindActionCreators({ initialize, reset }, dispatch),
  }),
  relay: {
    query,
    variables: {
      count: PAGE_SIZE,
    },
    create: () =>
      createPaginationContainer(
        JobRequests,
        {
          admin: graphql`
            fragment JobRequests_admin on Admin
              @argumentDefinitions(
                count: { type: "Int" }
                cursor: { type: "String" }
              ) {
              allStudents(first: 10, fuzzy: $searchStudent) {
                edges {
                  node {
                    id
                    user {
                      email
                    }
                    firstname
                    lastname
                  }
                }
              }
              allJobRequests(
                first: $count
                after: $cursor
                status: $status
                requestType: $requestType
                student: $studentId
                hasOpenJobOffers: $hasOpenJobOffers
              ) @connection(key: "JobRequests_allJobRequests") {
                edges {
                  node {
                    id
                    status
                    requestType
                    student {
                      firstname
                      lastname
                      profilePictureThumbnailUri
                    }
                    grade {
                      name
                    }
                    subject {
                      name
                    }
                    createdAt
                    openJobOffersCount
                    sessions {
                      timeSlots {
                        id
                        startDate
                        duration {
                          hours
                          minutes
                        }
                      }
                    }
                  }
                }
                pageInfo {
                  hasNextPage
                }
              }
            }
          `,
        },
        {
          direction: 'forward',
          getConnectionFromProps(props) {
            return props.admin && props.admin.allJobRequests;
          },
          getFragmentVariables(prevVars, totalCount) {
            return {
              ...prevVars,
              count: totalCount,
            };
          },
          getVariables(props, { count, cursor } /* , fragmentVariables */) {
            return {
              count,
              cursor,
            };
          },
          query,
        }
      ),
  },
});



// WEBPACK FOOTER //
// ./src/modules/job/JobRequests.js